/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-22 08:46:15
 * @ Modified by: David Helmick
 * @ Modified time: 2024-08-07 13:16:31
 * @ Description: Dialog to send a property to the trash
 */

import React, { useEffect, useState } from 'react'

import {
  Grid,
  ListItemButton,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  Typography,
  Box,
} from '@mui/material'
// Centralized lodash imports - much cleaner!
import { map } from '@/utils/lodash'

import { enqueueSnackbar } from 'notistack'
import { useMutation, useLazyQuery } from '@apollo/client/react'
import { GET_ORGS_PROPERTY_SHARED_WITH } from '../queries'
import { GET_PROPERTIES, GET_TRASH } from '../../Properties/queries'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { UPDATE_PROPERTIES } from '@/housefolios-components/Portfolio/mutations'
import withSession from '@/housefolios-components/Session/withSession'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'

function TrashPropertyComponent(props) {
  const { rightClickMenu } = props
  const [loader, setLoader] = useState(false)
  const [openDeleteProperty, setDeleteProperty] = useState(false)
  const handleOpenDeleteProperty = () => {
    setDeleteProperty(true)
  }
  const handleCloseDeleteProperty = () => {
    setDeleteProperty(false)
  }
  const [confirm, setConfirm] = useState(false)
  const [updateProperties] = useMutation(UPDATE_PROPERTIES)

  const handleTrash = () => {
    if (props.goToTrash)
      localStorage.setItem('portfolio', props.portfolioId)
    setLoader(true)
    updateProperties({
      variables: {
        ids: props.propertyId,
        propertyInput: {
          deleted: true,
          deletedAt: new Date(),
          deletedOrg: props.session.me.activeOrg._id,
        },
      },
      refetchQueries: [
        {
          query: GET_PROPERTIES,
          variables: {
            portfolioId: [props.portfolioId],
            limit: GET_PROPERTIES_QUERY_LIMIT,
          },
        },
        {
          query: GET_PROPERTIES,
          variables: {
            portfolioId: map(
              props.portfolios,
              (portfolio) => portfolio._id,
            ),
          },
        },
        {
          query: GET_PROPERTY,
          variables: {
            id: props.propertyId,
          },
        },
        {
          query: GET_TRASH,
        },
        {
          query: GET_PORTFOLIOS,
        },
        {
          query: GET_ME,
        },
      ],
    })
      .then(() => {
        enqueueSnackbar(`Selected Properties Trashed`, {
          variant: 'success',
          autoHideDuration: 3000,
        })
        handleCloseDeleteProperty()
        if (props.setSelected) props.setSelected([])
      })
      .catch((error) => {
        enqueueSnackbar(`Failed to Trash Selected Properties`, {
          variant: 'error',
          autoHideDuration: 3000,
        })
        handleCloseDeleteProperty()
      })
  }

  const [findSharedOrgs, results] = useLazyQuery(
    GET_ORGS_PROPERTY_SHARED_WITH,
  )

  const { data, error, loading, called } = results

  useEffect(() => {
    if (called && !loading) {
      if (orgsPropertySharedWith.length <= 0) handleTrash()
      else handleOpenDeleteProperty()
    }
  }, [called, loading])

  if (loading) return 'loading'
  if (error) return `Error! ${error.message}`
  const { orgsPropertySharedWith = [] } = data || {}

  return (
    <>
      {rightClickMenu && (
        <ListItemButton
          component="a"
          href={
            called &&
            props.goToTrash &&
            orgsPropertySharedWith.length <= 0
              ? '/trash'
              : null
          }
          onClick={
            !called
              ? () =>
                  findSharedOrgs({
                    variables: { propertyId: props.propertyId },
                  })
              : orgsPropertySharedWith.length <= 0
                ? handleTrash
                : handleOpenDeleteProperty
          }
          disabled={loader}

          sx={{ borderRadius: '0.29rem' }}
        >
          <Grid container direction="row" spacing={1}>
            <Grid data-hf-grid="keep">
              <FontAwesomeIcon icon={['fas', 'trash']} style={{ color: 'red', marginRight: 2 }} sx={{ fontSize: "1.15rem !important" }} />
            </Grid>
            <Grid data-hf-grid="keep">
              <Typography style={{ color: 'red' }}>
                Trash Property
              </Typography>
            </Grid>
          </Grid>
        </ListItemButton>
      )}
      <Dialog
        open={openDeleteProperty}
        onClose={handleCloseDeleteProperty}
      >
        <Box sx={{ textAlign: 'center', p: '3rem' }}>
          <Box sx={[{ borderRadius: '50%', m: 0 }, {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            mr: "0.25rem",
            position: "relative",
            fontSize: "1.2rem",
            transition: "all 0.2s ease-in-out",
            "&:last-child": { mr: 0, marginRight: 0 },
            "& .badge": { position: "absolute", right: 0, bottom: 0 },
            "& svg, & .svg-inline--fa": { display: "block" },
            marginRight: "0.25rem",
          }]}>
            <Box sx={[{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: 0,
              backgroundColor: "rgb(100%, 95.9215686275%, 96.418938307%) !important",
              height: "130px !important",
              width: "130px !important",
              lineHeight: "130px !important",
              p: 0,
              borderRadius: "50%",
              color: "#f83245",
              m: 0,
            }, {
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mr: "0.25rem",
              position: "relative",
              fontSize: "1.2rem",
              transition: "all 0.2s ease-in-out",
              "&:last-child": { mr: 0, marginRight: 0 },
              "& .badge": { position: "absolute", right: 0, bottom: 0 },
              "& svg, & .svg-inline--fa": { display: "block" },
              marginRight: "0.25rem",
            }]}>
              <FontAwesomeIcon icon={['far', 'trash-can']} size="10x" style={{ display: 'flex', alignSelf: 'center' }} sx={{ fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }} />
            </Box>
          </Box>
          <Box component="h4" sx={{ fontWeight: 700, mt: '1.5rem' }}>
            Trash Shared Property?
          </Box>
          <Box sx={[{ mb: 0, color: '#a6a6b9' }, { fontSize: "1.15rem !important" }]}>
            <FormControl component="fieldset" sx={{ pr: '1.5rem' }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirm}
                      onChange={() => setConfirm(!confirm)}
                    />
                  }
                  label="Remove this property for all shared users?"
                />
              </FormGroup>
            </FormControl>
            {/* Delete this portfolio along with all the properties inside. This can't be undone. */}
          </Box>
          <Box sx={{ pt: '1.5rem' }}>
            <Button
              variant="text"
              onClick={handleCloseDeleteProperty}
              //
            >
              <Box component="span" sx={{
                display: "inline-block",
                "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
              }}>Cancel</Box>
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: '#f83145', color: 'white' }}
              href={props.goToTrash ? '/trash' : null}
              onClick={handleTrash}
              disabled={!confirm}
            >
              <Box component="span" sx={{
                display: "inline-block",
                "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
              }}>Trash</Box>
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  )
}

export default withSession(TrashPropertyComponent)
